import { useState, useRef, useCallback } from 'react';

const MAX_FRAMES = 300; // 最大帧数限制（12.5秒 @ 24fps）

/**
 * 帧管理 Hook
 *
 * 核心设计：用 useRef 存储帧数据（同步读写），用 useState 触发 UI 更新。
 * 这样避免了 React state 异步更新导致的帧数据错乱问题。
 */
export default function useFrameManager(fabricCanvasRef, { onionSkinUpdate } = {}) {
  // 帧数据用 ref 存储，保证同步读写
  const framesRef = useRef([
    { index: 0, isKeyFrame: true, fabricJson: null, thumbnail: null },
  ]);
  const currentFrameIndexRef = useRef(0);

  // 用 state 快照驱动 UI 重渲染，ref 是 source of truth
  const [frameSnapshot, setFrameSnapshot] = useState({ frames: framesRef.current, idx: 0 });
  const triggerUpdate = useCallback(() => {
    setFrameSnapshot({ frames: [...framesRef.current], idx: currentFrameIndexRef.current });
  }, []);

  // 从画布捕获当前帧数据（同步）
  const captureCurrentFrame = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const idx = currentFrameIndexRef.current;
    const frame = framesRef.current[idx];
    if (!frame) return;

    frame.fabricJson = JSON.stringify(canvas.toJSON());
    frame.thumbnail = canvas.toDataURL({ format: 'png', multiplier: 0.15 });
  }, [fabricCanvasRef]);

  // 将指定帧加载到画布（同步）
  const loadFrameToCanvas = useCallback((frameIndex) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const frame = framesRef.current[frameIndex];

    // 彻底清空画布
    canvas.discardActiveObject();
    canvas.getObjects().slice().forEach(obj => canvas.remove(obj));
    canvas.backgroundColor = '#ffffff'; // fabric 画布底色（画布内容背景，与主题无关，保留）

    if (frame?.fabricJson) {
      canvas.loadFromJSON(frame.fabricJson, () => {
        canvas.renderAll();
        onionSkinUpdate?.(frameIndex, framesRef.current);
      });
    } else {
      canvas.renderAll();
      onionSkinUpdate?.(frameIndex, framesRef.current);
    }
  }, [fabricCanvasRef, onionSkinUpdate]);

  // 切换帧
  const switchToFrame = useCallback((targetIndex) => {
    if (targetIndex < 0 || targetIndex >= framesRef.current.length) return;
    if (!fabricCanvasRef.current) return;

    // 1. 保存当前帧（同步写入 ref）
    captureCurrentFrame();

    // 2. 更新索引
    currentFrameIndexRef.current = targetIndex;

    // 3. 加载目标帧到画布
    loadFrameToCanvas(targetIndex);

    // 4. 触发 UI 更新
    triggerUpdate();
  }, [fabricCanvasRef, captureCurrentFrame, loadFrameToCanvas, triggerUpdate]);

  // 下一帧
  const nextFrame = useCallback(() => {
    const next = currentFrameIndexRef.current + 1;
    if (next < framesRef.current.length) {
      switchToFrame(next);
    }
  }, [switchToFrame]);

  // 上一帧
  const prevFrame = useCallback(() => {
    const prev = currentFrameIndexRef.current - 1;
    if (prev >= 0) {
      switchToFrame(prev);
    }
  }, [switchToFrame]);

  // 添加空白帧（在当前帧后面）
  const addBlankFrame = useCallback(() => {
    if (framesRef.current.length >= MAX_FRAMES) return;

    // 1. 保存当前帧
    captureCurrentFrame();

    // 2. 插入空白帧
    const insertAt = currentFrameIndexRef.current + 1;
    framesRef.current.splice(insertAt, 0, {
      index: insertAt,
      isKeyFrame: false,
      fabricJson: null,
      thumbnail: null,
    });

    // 3. 重新编号
    framesRef.current.forEach((f, i) => { f.index = i; });

    // 4. 切换到新帧（会清空画布并加载空白帧）
    currentFrameIndexRef.current = insertAt;
    loadFrameToCanvas(insertAt);

    triggerUpdate();
  }, [captureCurrentFrame, loadFrameToCanvas, triggerUpdate]);

  // 复制当前帧
  const duplicateFrame = useCallback(() => {
    if (framesRef.current.length >= MAX_FRAMES) return;

    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // 1. 捕获当前画布内容
    const json = JSON.stringify(canvas.toJSON());
    const thumbnail = canvas.toDataURL({ format: 'png', multiplier: 0.15 });

    // 同时保存到当前帧
    captureCurrentFrame();

    // 2. 插入副本
    const insertAt = currentFrameIndexRef.current + 1;
    framesRef.current.splice(insertAt, 0, {
      index: insertAt,
      isKeyFrame: false,
      fabricJson: json,
      thumbnail,
    });

    // 3. 重新编号
    framesRef.current.forEach((f, i) => { f.index = i; });

    // 4. 切换到新帧
    currentFrameIndexRef.current = insertAt;
    loadFrameToCanvas(insertAt);

    triggerUpdate();
  }, [fabricCanvasRef, captureCurrentFrame, loadFrameToCanvas, triggerUpdate]);

  // 删除当前帧
  const deleteFrame = useCallback(() => {
    if (framesRef.current.length <= 1) return;

    const deleteIdx = currentFrameIndexRef.current;

    // 1. 删除帧
    framesRef.current.splice(deleteIdx, 1);

    // 2. 重新编号
    framesRef.current.forEach((f, i) => { f.index = i; });

    // 3. 切换到合适的帧
    const newIdx = Math.min(deleteIdx, framesRef.current.length - 1);
    currentFrameIndexRef.current = newIdx;
    loadFrameToCanvas(newIdx);

    triggerUpdate();
  }, [loadFrameToCanvas, triggerUpdate]);

  // 切换关键帧标记
  const toggleKeyFrame = useCallback(() => {
    const idx = currentFrameIndexRef.current;
    const frame = framesRef.current[idx];
    if (frame) {
      frame.isKeyFrame = !frame.isKeyFrame;
      triggerUpdate();
    }
  }, [triggerUpdate]);

  // 保存当前帧到内存（供外部调用，如导出前）
  const saveCurrentFrame = useCallback(() => {
    captureCurrentFrame();
    triggerUpdate();
  }, [captureCurrentFrame, triggerUpdate]);

  // 加载帧到画布（兼容旧接口）
  const loadFrame = useCallback((frameIndex) => {
    loadFrameToCanvas(frameIndex);
  }, [loadFrameToCanvas]);

  // 获取第一帧缩略图
  const getFirstFrameThumbnail = useCallback(() => {
    return framesRef.current[0]?.thumbnail || null;
  }, []);

  // 批量添加帧
  const addMultipleFrames = useCallback((count) => {
    const total = Math.min(framesRef.current.length + count, MAX_FRAMES);
    const toAdd = total - framesRef.current.length;
    if (toAdd <= 0) return;

    for (let i = 0; i < toAdd; i++) {
      framesRef.current.push({
        index: framesRef.current.length,
        isKeyFrame: false,
        fabricJson: null,
        thumbnail: null,
      });
    }
    triggerUpdate();
  }, [triggerUpdate]);

  // 清空所有帧
  const clearAllFrames = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      canvas.discardActiveObject();
      canvas.getObjects().slice().forEach(obj => canvas.remove(obj));
      canvas.backgroundColor = '#ffffff'; // fabric 画布底色（画布内容背景，与主题无关，保留） // fabric 画布底色（画布内容背景，与主题无关，保留）
      canvas.renderAll();
    }

    framesRef.current = [{ index: 0, isKeyFrame: true, fabricJson: null, thumbnail: null }];
    currentFrameIndexRef.current = 0;
    triggerUpdate();
  }, [fabricCanvasRef, triggerUpdate]);

  // 从外部数据恢复帧（用于重新打开编辑器时加载已保存的帧）
  const restoreFrames = useCallback((savedFrames) => {
    if (!savedFrames || savedFrames.length === 0) return;

    framesRef.current = savedFrames.map((f, i) => ({
      index: i,
      isKeyFrame: f.isKeyFrame || false,
      fabricJson: f.fabricJson || null,
      thumbnail: f.thumbnail || null,
    }));
    currentFrameIndexRef.current = 0;

    // 加载第一帧到画布
    loadFrameToCanvas(0);
    triggerUpdate();
  }, [loadFrameToCanvas, triggerUpdate]);

  // 导出所有帧数据（用于持久化保存）
  const exportFrames = useCallback(() => {
    captureCurrentFrame();
    return framesRef.current.map(f => ({
      index: f.index,
      isKeyFrame: f.isKeyFrame,
      fabricJson: f.fabricJson,
      thumbnail: f.thumbnail,
    }));
  }, [captureCurrentFrame]);

  return {
    frames: frameSnapshot.frames,
    currentFrameIndex: frameSnapshot.idx,
    currentFrameIndexRef,
    switchToFrame,
    nextFrame,
    prevFrame,
    addBlankFrame,
    duplicateFrame,
    deleteFrame,
    toggleKeyFrame,
    saveCurrentFrame,
    loadFrame,
    getFirstFrameThumbnail,
    addMultipleFrames,
    clearAllFrames,
    restoreFrames,
    exportFrames,
    MAX_FRAMES,
  };
}
